import React, { useState, useEffect } from 'react';
import { MessageCircle, X, Minimize2, Maximize2, Users } from 'lucide-react';
import { useChat } from '../../hooks/useChat';
import ConversasList from './ConversasList';
import MensagensContainer from './MensagensContainer';
import ChatInput from './ChatInput';
import ChatHeader from './ChatHeader';
import UsuariosOnlineList from './UsuariosOnlineList';
import toast from 'react-hot-toast';

const FloatingChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showConversas, setShowConversas] = useState(true);
  const [abaSelecionada, setAbaSelecionada] = useState('usuarios'); 

  const {
    conversas,
    conversaAtual,
    mensagens,
    digitando,
    conectado,
    carregando,
    erro,
    user,
    selecionarConversa,
    enviarMensagem,
    indicarDigitando,
    isOutroUsuarioOnline,
    isUsuarioOnline,
    limparErro,
  } = useChat();

  // Mostra erros
  useEffect(() => {
    if (erro) {
      toast.error(erro);
      limparErro();
    }
  }, [erro]);

  const totalNaoLidas = conversas.reduce(
    (acc, conv) => acc + (conv.mensagensNaoLidas || 0),
    0
  );

  const handleSelecionarConversa = (conversa) => {
    selecionarConversa(conversa);
    setShowConversas(false);
  };

  const handleVoltar = () => {
    setShowConversas(true);
  };

  const handleEnviarMensagem = (conteudo) => {
    enviarMensagem(conteudo);
  };

  const handleIniciarConversa = (usuario) => {
    const novaConversa = {
      conversaId: null,
      outroUsuario: usuario,
      ultimaMensagem: null,
      mensagensNaoLidas: 0,
    };
    
    handleSelecionarConversa(novaConversa);
    setAbaSelecionada('conversas');
  };

  useEffect(() => {
    }, [conversas, conversaAtual, mensagens, conectado]);

  if (!user) return null;

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-14 right-6 z-50 w-14 h-14 bg-primary-500 hover:bg-primary-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
        title="Abrir chat"
      >
        <MessageCircle className="w-6 h-6" />
        {totalNaoLidas > 0 && (
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {totalNaoLidas > 99 ? '99+' : totalNaoLidas}
          </span>
        )}
        <span className="absolute right-full mr-3 px-3 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Chat
        </span>
      </button>
    );
  }

  // Chat aberto
  return (
    <div
      className={`fixed z-50 transition-all duration-200 ${
        isMinimized
          ? 'bottom-8 right-6 w-80'
          : 'bottom-8 right-6 w-96 h-[600px] md:w-[800px]'
      }`}
    >
      <div
        className={`bg-white dark:bg-dark-card rounded-lg shadow-2xl border border-gray-200 dark:border-dark-border overflow-hidden flex flex-col ${
          isMinimized ? 'h-14' : 'h-full'
        }`}
      >
        {/* Header do Chat Flutuante */}
        <div className="flex items-center justify-between p-3 bg-primary-500 text-white border-b border-primary-600">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            <span className="font-semibold">Chat</span>
            {totalNaoLidas > 0 && !isMinimized && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {totalNaoLidas}
              </span>
            )}
            {!conectado && !isMinimized && (
              <span className="text-xs text-white/70 ml-2">
                (Desconectado)
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1.5 hover:bg-white/10 rounded transition-colors"
              title={isMinimized ? 'Maximizar' : 'Minimizar'}
            >
              {isMinimized ? (
                <Maximize2 className="w-4 h-4" />
              ) : (
                <Minimize2 className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 hover:bg-white/10 rounded transition-colors"
              title="Fechar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Conteúdo do Chat */}
        {!isMinimized && (
          <div className="flex-1 flex overflow-hidden">
            {/* Layout Desktop - Três colunas */}
            <div className="hidden md:flex flex-1 overflow-hidden">
              {/* Sidebar com Abas */}
              <div className="w-80 border-r border-gray-200 dark:border-dark-border flex flex-col">
                {/* Abas */}
                <div className="flex border-b border-gray-200 dark:border-dark-border">
                  <button
                    onClick={() => setAbaSelecionada('usuarios')}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                      abaSelecionada === 'usuarios'
                        ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                        : 'text-gray-600 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-bg'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>Usuários</span>
                    </div>
                    {abaSelecionada === 'usuarios' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" />
                    )}
                  </button>
                  
                  <button
                    onClick={() => setAbaSelecionada('conversas')}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                      abaSelecionada === 'conversas'
                        ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                        : 'text-gray-600 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-bg'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      <span>Conversas</span>
                      {totalNaoLidas > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                          {totalNaoLidas > 99 ? '99+' : totalNaoLidas}
                        </span>
                      )}
                    </div>
                    {abaSelecionada === 'conversas' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" />
                    )}
                  </button>
                </div>

                {/* Conteúdo das Abas */}
                <div className="flex-1 overflow-hidden">
                  {abaSelecionada === 'conversas' ? (
                    <ConversasList
                      conversas={conversas}
                      conversaAtual={conversaAtual}
                      onSelecionarConversa={handleSelecionarConversa}
                      usuariosOnline={new Set(
                        conversas
                          .map((c) => c.outroUsuario.id)
                          .filter((id) => isUsuarioOnline(id))
                      )}
                    />
                  ) : (
                    <UsuariosOnlineList
                      onIniciarConversa={handleIniciarConversa}
                      usuariosOnline={new Set(
                        Array.from({ length: 100 }, (_, i) => i + 1).filter((id) =>
                          isUsuarioOnline(id)
                        )
                      )}
                    />
                  )}
                </div>
              </div>

              {/* Área de Chat */}
              <div className="flex-1 flex flex-col">
                {conversaAtual ? (
                  <>
                    <ChatHeader
                      conversaAtual={conversaAtual}
                      online={isOutroUsuarioOnline()}
                    />
                    <MensagensContainer
                      mensagens={mensagens}
                      userId={user.id}
                      digitando={digitando}
                      nomeDigitando={conversaAtual?.outroUsuario.nome}
                    />
                    <ChatInput
                      onEnviarMensagem={handleEnviarMensagem}
                      onDigitando={indicarDigitando}
                      disabled={!conectado}
                    />
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-dark-muted">
                    <div className="text-center">
                      <MessageCircle className="w-16 h-16 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Selecione uma conversa</p>
                      <p className="text-xs mt-1">
                        para começar a conversar
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Layout Mobile - Uma coluna */}
            <div className="flex md:hidden flex-1 flex-col overflow-hidden">
              {showConversas ? (
                <>
                  {/* Abas Mobile */}
                  <div className="flex border-b border-gray-200 dark:border-dark-border">
                    <button
                      onClick={() => setAbaSelecionada('usuarios')}
                      className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors relative ${
                        abaSelecionada === 'usuarios'
                          ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                          : 'text-gray-600 dark:text-dark-muted'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <Users className="w-3.5 h-3.5" />
                        <span>Usuários</span>
                      </div>
                      {abaSelecionada === 'usuarios' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" />
                      )}
                    </button>
                    
                    <button
                      onClick={() => setAbaSelecionada('conversas')}
                      className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors relative ${
                        abaSelecionada === 'conversas'
                          ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                          : 'text-gray-600 dark:text-dark-muted'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <MessageCircle className="w-3.5 h-3.5" />
                        <span>Conversas</span>
                        {totalNaoLidas > 0 && (
                          <span className="bg-red-500 text-white text-xs font-bold px-1 py-0.5 rounded-full min-w-[16px] text-center">
                            {totalNaoLidas > 9 ? '9+' : totalNaoLidas}
                          </span>
                        )}
                      </div>
                      {abaSelecionada === 'conversas' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" />
                      )}
                    </button>
                  </div>

                  {/* Conteúdo das Abas Mobile */}
                  {abaSelecionada === 'conversas' ? (
                    <ConversasList
                      conversas={conversas}
                      conversaAtual={conversaAtual}
                      onSelecionarConversa={handleSelecionarConversa}
                      usuariosOnline={new Set(
                        conversas
                          .map((c) => c.outroUsuario.id)
                          .filter((id) => isUsuarioOnline(id))
                      )}
                    />
                  ) : (
                    <UsuariosOnlineList
                      onIniciarConversa={handleIniciarConversa}
                      usuariosOnline={new Set(
                        Array.from({ length: 100 }, (_, i) => i + 1).filter((id) =>
                          isUsuarioOnline(id)
                        )
                      )}
                    />
                  )}
                </>
              ) : conversaAtual ? (
                <>
                  <ChatHeader
                    conversaAtual={conversaAtual}
                    online={isOutroUsuarioOnline()}
                    onFechar={handleVoltar}
                  />
                  <MensagensContainer
                    mensagens={mensagens}
                    userId={user.id}
                    digitando={digitando}
                    nomeDigitando={conversaAtual?.outroUsuario.nome}
                  />
                  <ChatInput
                    onEnviarMensagem={handleEnviarMensagem}
                    onDigitando={indicarDigitando}
                    disabled={!conectado}
                  />
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingChat;
